/**
 * FotoReunion v3.2.B — Foto del antes · paso 3 de 4 (IMPL-028 · 2026-05-05).
 *
 * Rediseño aprobado: tabla lineal "problema → solución MOBILSAFE" + panel sticky derecho.
 *
 * Layout (lg+):
 *   ┌─────────────────────────────────────┬─────────────┐
 *   │ KPI strip (debilidades · cuestion · flota)         │
 *   ├─────────────────────────────────────┼─────────────┤
 *   │ Tabla lineal por eje                │ Panel sticky│
 *   │  ⚠️ Hoy · Lo que falta │→│ ✦ MOBILSAFE│ - Resumen   │
 *   │  Problema rojo          │→│ Solución verde │ - Observ. │
 *   │  ...                                │ - CTA       │
 *   └─────────────────────────────────────┴─────────────┘
 *
 * Soluciones MOBILSAFE: vienen de `biblioteca_diagnostico` (IMPL-028, backend).
 * Si una debilidad no tiene match en la biblioteca, se muestra placeholder editable.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  AlertTriangle, CheckCircle2, Truck,
  Shield, Fuel, Gauge, Wrench, Monitor, Leaf, Sparkles, ArrowRight, ChevronRight,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const META_EJES = [
  { id: "seguridad",      nombre: "Seguridad y Riesgo",       Icon: Shield,  grad: "from-emerald-400 to-green-500" },
  { id: "combustible",    nombre: "Combustible y Conducción", Icon: Fuel,    grad: "from-orange-400 to-red-500" },
  { id: "operativa",      nombre: "Operativa y Control",      Icon: Gauge,   grad: "from-blue-400 to-cyan-500" },
  { id: "mantenimiento",  nombre: "Mantenimiento",            Icon: Wrench,  grad: "from-amber-400 to-yellow-500" },
  { id: "tic",            nombre: "TIC y Digitalización",     Icon: Monitor, grad: "from-violet-400 to-purple-500" },
  { id: "sostenibilidad", nombre: "Sostenibilidad y CAES",    Icon: Leaf,    grad: "from-emerald-400 to-green-500" },
];

const DEBILIDAD_KEYWORDS = [
  /\bno\b/i, /\bsin\b/i, /\balta\b/i, /\bnunca\b/i,
  /espor[áa]dic/i, /reactiv/i, /excel/i, /papel/i, /ningun/i,
  /bajo\b/i, /mal\b/i, /poco\b/i, /nul[oa]/i, /no lo s/i,
];

function esDebilidadOpcion(opcion) {
  if (!opcion) return false;
  return DEBILIDAD_KEYWORDS.some((rx) => rx.test(opcion));
}

const PLACEHOLDER_SOL = "Solución MOBILSAFE pendiente de completar (admin · Configuración)";

function authAxios() {
  const token = localStorage.getItem("mobilsafe_token");
  return axios.create({ headers: token ? { Authorization: `Bearer ${token}` } : {} });
}

export default function FotoReunion({ reunion, cliente, preguntasCfg = [], onGuardarObservaciones, onGenerarDiagnostico }) {
  const respuestas = reunion?.respuestas_cuestionario || [];
  const flotaAprox = reunion?.flota_aproximada || {};
  const flotaConf = reunion?.flota_confirmada || {};
  const [biblioteca, setBiblioteca] = useState([]);

  // Cargar biblioteca de diagnóstico (IMPL-028)
  useEffect(() => {
    let cancelled = false;
    authAxios().get(`${API}/configuracion-fase2/biblioteca-diagnostico`)
      .then((res) => { if (!cancelled) setBiblioteca(res.data?.items || []); })
      .catch(() => { /* silencioso · usaremos placeholder */ });
    return () => { cancelled = true; };
  }, []);

  // Mapa pregunta_id → pregunta config (para textos de las preguntas)
  const preguntaPorId = useMemo(() => {
    const m = {};
    preguntasCfg.forEach((p) => { m[p.id] = p; });
    return m;
  }, [preguntasCfg]);

  // Búsqueda en biblioteca por (pregunta_id + opcion_detonante)
  const buscarSolucion = (pregunta_id, opcion) => {
    const item = biblioteca.find(
      (b) => b.activo !== false && b.pregunta_id === pregunta_id && b.opcion_detonante === opcion
    );
    return item || null;
  };

  // Agrupar respuestas por eje + detectar debilidades + matching biblioteca
  const ejesDatos = useMemo(() => {
    return META_EJES.map((meta) => {
      const respuestasEje = respuestas.filter((r) => r.eje === meta.id);
      const totalEje = preguntasCfg.filter((p) => p.eje === meta.id && p.activa !== false).length;
      const sinContestar = Math.max(0, totalEje - respuestasEje.length);

      const debilidades = respuestasEje
        .filter((r) => r.opcion && esDebilidadOpcion(r.opcion))
        .map((r) => {
          const preg = preguntaPorId[r.pregunta_id] || {};
          const bib = buscarSolucion(r.pregunta_id, r.opcion);
          return {
            pregunta_id: r.pregunta_id,
            pregunta: preg.texto || r.pregunta_id,
            valor: r.opcion,
            nota: r.notas_libre,
            problema_texto: bib?.problema_texto || preg.texto || r.pregunta_id,
            solucion_texto: bib?.solucion_texto || PLACEHOLDER_SOL,
            kpi_estimado: bib?.kpi_estimado || null,
          };
        });

      return { ...meta, contestadas: respuestasEje.length, total: totalEje, sinContestar, debilidades };
    });
  }, [respuestas, preguntasCfg, preguntaPorId, biblioteca]); // eslint-disable-line

  const ejesConDeb = ejesDatos.filter((e) => e.debilidades.length > 0);
  const ejesSinDeb = ejesDatos.filter((e) => e.debilidades.length === 0);

  const totalDebilidades = ejesDatos.reduce((s, e) => s + e.debilidades.length, 0);
  const totalContestadas = ejesDatos.reduce((s, e) => s + e.contestadas, 0);
  const totalPreguntas = ejesDatos.reduce((s, e) => s + e.total, 0);

  const dato = (k) => {
    const conf = flotaConf[k];
    if (conf && (conf.valor !== null && conf.valor !== undefined)) return conf.valor;
    return flotaAprox[k];
  };
  const num = (v) => (v === null || v === undefined || v === "") ? "—" : Number(v).toLocaleString("es-ES");
  const numVehiculos = dato("num_vehiculos");

  return (
    <div className="bg-slate-50 text-slate-800 min-h-[calc(100vh-64px)]" data-testid="foto-reunion">
      <main className="max-w-7xl mx-auto px-6 py-5 space-y-3">

        {/* KPI strip · sin cambios de IMPL-026 */}
        <div className="bg-white border border-slate-200 rounded-2xl p-3 flex items-center gap-4 flex-wrap shadow-sm">
          <KpiBlock testid="kpi-debilidades" Icon={AlertTriangle} color="rose" label="Debilidades" value={totalDebilidades} />
          <Divider />
          <KpiBlock testid="kpi-cuestionario" Icon={CheckCircle2} color="emerald" label="Cuestionario" value={`${totalContestadas} / ${totalPreguntas}`} />
          <Divider />
          <KpiBlock testid="kpi-flota" Icon={Truck} color="blue" label="Flota" value={numVehiculos ? `${num(numVehiculos)} veh.` : "—"} />
          <div className="h-9 w-px bg-slate-200 hidden md:block" />
          <div className="text-xs text-slate-500 hidden md:block">
            {cliente?.sector || "—"} · {reunion?.fecha_reunion ? new Date(reunion.fecha_reunion).toLocaleDateString("es-ES") : new Date(reunion?.created_at || Date.now()).toLocaleDateString("es-ES")}
          </div>
        </div>

        {/* Layout principal · grid 1fr 260px en lg+, stacked en móvil */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-3" data-testid="foto-reunion-main">

          {/* === ZONA PRINCIPAL · Tabla lineal "problema → solución" === */}
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden" data-testid="tabla-debilidades">

            {/* Cabecera tabla */}
            <div className="grid grid-cols-[1fr_36px_1fr] border-b-2 border-slate-200">
              <div className="bg-rose-50 px-4 py-3">
                <h3 className="text-[11px] font-bold uppercase tracking-widest text-rose-700 flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" /> Hoy · Lo que falta
                </h3>
              </div>
              <div className="bg-slate-50 border-x border-slate-100" />
              <div className="bg-emerald-50 px-4 py-3">
                <h3 className="text-[11px] font-bold uppercase tracking-widest text-emerald-700 flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5" /> Con MOBILSAFE · Solución
                </h3>
              </div>
            </div>

            {/* Ejes con debilidades · 1 cabecera + N filas */}
            {ejesConDeb.map((eje) => {
              const Icon = eje.Icon;
              return (
                <div key={eje.id} data-testid={`eje-${eje.id}-block`}>
                  {/* Cabecera eje · misma fila izda + dcha */}
                  <div className="grid grid-cols-[1fr_36px_1fr] border-b border-slate-100">
                    <div className="bg-red-50 border-l-4 border-red-500 px-4 py-2.5 flex items-center gap-2">
                      <div className={`w-6 h-6 bg-gradient-to-br ${eje.grad} rounded-md flex items-center justify-center text-white shadow-sm`}>
                        <Icon className="w-3 h-3" />
                      </div>
                      <span className="font-bold text-sm text-slate-800">{eje.nombre}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-200 text-red-800 font-mono font-bold">{eje.debilidades.length}</span>
                    </div>
                    <div className="bg-slate-50 border-x border-slate-100" />
                    <div className="bg-green-50 border-l-4 border-green-600 px-4 py-2.5 flex items-center gap-2">
                      <div className={`w-6 h-6 bg-gradient-to-br ${eje.grad} rounded-md flex items-center justify-center text-white shadow-sm`}>
                        <Icon className="w-3 h-3" />
                      </div>
                      <span className="font-bold text-sm text-slate-800">{eje.nombre}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-200 text-green-800 font-mono font-bold">{eje.debilidades.length}</span>
                    </div>
                  </div>

                  {/* Filas debilidad → solución */}
                  {eje.debilidades.map((d, idx) => (
                    <div key={`${d.pregunta_id}-${idx}`} className="grid grid-cols-[1fr_36px_1fr] border-b border-slate-100"
                         data-testid={`fila-${eje.id}-${idx}`}>
                      <div className="bg-rose-50/30 px-4 py-3 flex flex-col gap-1">
                        <p className="text-sm font-semibold text-slate-800">{d.problema_texto}</p>
                        <p className="text-xs text-rose-600 font-mono">✗ {d.valor}</p>
                        {d.nota && <p className="text-[11px] text-slate-500 italic">"{d.nota}"</p>}
                      </div>
                      <div className="bg-slate-50 border-x border-slate-100 flex items-center justify-center">
                        <ArrowRight className="w-4 h-4 text-slate-400" />
                      </div>
                      <div className="bg-emerald-50/30 px-4 py-3 flex flex-col gap-1">
                        <p className="text-sm text-slate-800">{d.solucion_texto}</p>
                        {d.kpi_estimado && (
                          <p className="text-[11px] text-emerald-700 font-semibold">{d.kpi_estimado}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}

            {/* Ejes sin debilidades · al final */}
            {ejesSinDeb.map((eje) => {
              const Icon = eje.Icon;
              return (
                <div key={eje.id} className="grid grid-cols-[1fr_36px_1fr] border-b border-slate-100 last:border-0"
                     data-testid={`eje-${eje.id}-sin-deb`}>
                  <div className="bg-emerald-50 border-l-4 border-emerald-500 px-4 py-2.5 flex items-center gap-2">
                    <div className={`w-6 h-6 bg-gradient-to-br ${eje.grad} rounded-md flex items-center justify-center text-white shadow-sm`}>
                      <Icon className="w-3 h-3" />
                    </div>
                    <span className="font-bold text-sm text-slate-800">{eje.nombre}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-200 text-emerald-800 font-bold uppercase tracking-wider">✓ Sin debilidades</span>
                  </div>
                  <div className="bg-slate-50 border-x border-slate-100 flex items-center justify-center">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  </div>
                  <div className="bg-emerald-50/30 px-4 py-2.5 flex items-center">
                    <p className="text-xs text-slate-500 italic">Área sin oportunidades de mejora detectadas</p>
                  </div>
                </div>
              );
            })}

            {/* Estado vacío · sin debilidades en ningún sitio */}
            {ejesConDeb.length === 0 && ejesSinDeb.length === 0 && (
              <div className="p-12 text-center text-sm text-slate-400 italic">
                Cuestionario sin respuestas todavía · vuelve al paso 2 y rellena.
              </div>
            )}
          </div>

          {/* === PANEL DERECHO · sticky === */}
          <aside className="lg:sticky lg:top-[88px] self-start space-y-3" data-testid="panel-resumen">

            {/* Bloque 1 · Resumen */}
            <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
              <h3 className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Resumen</h3>
              <div className="text-center py-2">
                <div className={`text-4xl font-black ${totalDebilidades > 0 ? "text-rose-500" : "text-emerald-500"}`} data-testid="panel-total-debilidades">
                  {totalDebilidades}
                </div>
                <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-1">
                  oportunidades detectadas
                </div>
              </div>
              <div className="space-y-1.5 mt-3 pt-3 border-t border-slate-100">
                {[...ejesConDeb, ...ejesSinDeb].map((eje) => {
                  const Icon = eje.Icon;
                  const max = Math.max(1, ...ejesDatos.map((e) => e.debilidades.length));
                  const widthPct = (eje.debilidades.length / max) * 100;
                  const barColor = eje.debilidades.length === 0 ? "bg-emerald-300" : "bg-rose-400";
                  return (
                    <div key={eje.id} className="flex items-center gap-2 text-xs">
                      <Icon className="w-3 h-3 text-slate-500 flex-shrink-0" />
                      <span className="flex-1 truncate text-slate-700">{eje.nombre.split(" y ")[0].split(" · ")[0]}</span>
                      <div className="w-12 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full ${barColor} transition-all`} style={{ width: `${widthPct}%` }} />
                      </div>
                      <span className={`w-4 text-right font-mono font-bold ${eje.debilidades.length === 0 ? "text-emerald-600" : "text-rose-600"}`}>
                        {eje.debilidades.length}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Bloque 2 · Observaciones · autosave existente */}
            <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
              <h3 className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Observaciones</h3>
              <textarea
                rows={4}
                data-testid="foto-observaciones"
                defaultValue={reunion?.observaciones_reunion || ""}
                onBlur={(e) => onGuardarObservaciones && onGuardarObservaciones(e.target.value)}
                placeholder="Notas globales del comercial. Cosas que no encajaban en ninguna pregunta…"
                className="w-full px-2.5 py-1.5 border border-slate-200 rounded-lg text-xs focus:outline-none focus:border-emerald-500 resize-none"
              />
              <p className="text-[10px] text-slate-400 mt-1">Autoguardado al salir del campo</p>
            </div>

            {/* Bloque 3 · CTA · botón ancho completo */}
            {onGenerarDiagnostico && (
              <button
                onClick={onGenerarDiagnostico}
                data-testid="panel-generar-diagnostico"
                className="w-full px-4 py-3 bg-gradient-to-r from-emerald-500 to-green-500 text-white rounded-xl font-bold text-sm hover:from-emerald-600 hover:to-green-600 transition shadow-lg shadow-emerald-500/30 inline-flex items-center justify-center gap-1.5"
              >
                Generar Diagnóstico IA <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </aside>
        </div>

        <p className="text-center text-[10px] text-slate-400 pt-4 pb-6 uppercase tracking-widest">
          Foto del antes · v3.2.B · soluciones desde biblioteca de diagnóstico
        </p>
      </main>
    </div>
  );
}

// ============ Sub-componentes (KPI strip · sin cambios) ============

function KpiBlock({ Icon, color, label, value, testid }) {
  const cls = {
    rose: "bg-rose-50 text-rose-500",
    emerald: "bg-emerald-50 text-emerald-500",
    blue: "bg-blue-50 text-blue-500",
  }[color] || "bg-slate-50 text-slate-500";
  return (
    <div className="flex items-center gap-2.5" data-testid={testid}>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${cls}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold leading-none">{label}</div>
        <div className="text-lg font-black tracking-tight text-slate-900 leading-tight">{value}</div>
      </div>
    </div>
  );
}

function Divider() {
  return <div className="h-9 w-px bg-slate-200" />;
}
